import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import LockIcon from '@mui/icons-material/Lock';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEVICE_TYPE, ITableLock, ITables } from '../../all-types';
import SearchInput from '../../components/Input/SearchInput/SearchInput';
import { BB_BACKGROUND_IMGH } from '../../constants';
import { useProductSearch } from '../../hooks';
import { createBillSession, restoreBillSession } from '../../redux-store';
import { getTableById, lockTable, unlockTable } from '../../services';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { BillingScreen } from './billing';
import { TableAssigner } from './modules';
import { TAB_STYLES } from './styles';

interface TabData {
    uniqueTableName: string;
    label: string;
    floor: number;
    no: number;
    tableId: string;
}

const BillingInTabs: React.FC = () => {
    const theme = useTheme();
    const { t } = useTranslation("global");
    const UNASSIGNED_TAB = t("billing-section.tabs.unassigned-tab");
    const dispatcher = useAppDispatch();
    const [tabs, setTabs] = useState<TabData[]>([]);
    const activeBills = useAppSelector(state => state.billing.activeBills);
    const preloadTables = useAppSelector(state => state.preloadData.tables);
    const [currentTab, setCurrentTab] = useState<number>(0);
    const userId = useAppSelector(state => state.authentication.userId);
    const username = useAppSelector(state => state.authentication.username);
    const [lockConflict, setLockConflict] = useState<{ lockInfo: ITableLock; pendingTable: ITables } | null>(null);

    const { filteredProducts: products, searchTxt,
        searchTheProduct, setCategoryTxt, categories } = useProductSearch();

    const handleAddTab = () => {
        setTabs([...tabs, {
            label: `${UNASSIGNED_TAB}`,
            uniqueTableName: "", floor: 0, no: 0, tableId: ""
        }]);
        setCurrentTab(tabs.length);
    };

    const handleRemoveTab = (index: number) => {
        const tab = tabs[index];
        if (tab?.tableId) {
            dispatcher(unlockTable({ tableId: tab.tableId, lockedBy: userId })).catch(() => {});
        }
        setTabs((prevTabs) => prevTabs.filter((_, tabIndex) => tabIndex !== index));
        if (index === currentTab) {
            setCurrentTab(Math.max(0, index - 1));
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const openTableInTab = async (table: ITables) => {
        const { tableName, tableNo, floorNo, _id: tableId } = table;
        const tableUniqueName = tableName.concat(String(tableNo)).concat(String(floorNo));
        // Always resolve _id from preloadTables — clientInfo tables may not carry _id
        const liveTable = preloadTables.find(t =>
            t.tableName === tableName && t.tableNo === tableNo && t.floorNo === floorNo
        );
        const resolvedTableId = liveTable?._id || tableId || "";
        const tempTabs = [...tabs];
        tempTabs[currentTab].label = tableName;
        tempTabs[currentTab].no = tableNo;
        tempTabs[currentTab].floor = floorNo;
        tempTabs[currentTab].uniqueTableName = tableUniqueName;
        tempTabs[currentTab].tableId = resolvedTableId;

        if (!activeBills[tableUniqueName]) {
            if (liveTable?._id) {
                try {
                    const latestTable = await dispatcher(getTableById(liveTable._id)).unwrap();
                    if (latestTable?.billSession) {
                        dispatcher(restoreBillSession({ sessionId: tableUniqueName, bill: latestTable.billSession }));
                    } else {
                        dispatcher(createBillSession(tableUniqueName));
                    }
                } catch {
                    dispatcher(createBillSession(tableUniqueName));
                }
            } else {
                dispatcher(createBillSession(tableUniqueName));
            }
        }
        setTabs(tempTabs);
    };

    const handleTableSelect = async (table: ITables) => {
        try {
            const { tableName, tableNo, floorNo } = table;
            const tableUniqueName = tableName.concat(String(tableNo)).concat(String(floorNo));

            // Already open in another tab — switch to it and close the blank one
            const recordIndex = tabs?.findIndex(t => t.uniqueTableName === tableUniqueName);
            if (recordIndex !== -1) {
                handleRemoveTab(tabs.length - 1);
                setCurrentTab(recordIndex);
                return;
            }

            // Always use the live table for the _id (preloadTables is the authoritative source)
            const liveTable = preloadTables.find(t =>
                t.tableName === tableName && t.tableNo === tableNo && t.floorNo === floorNo
            );
            const tableId = liveTable?._id || table._id;

            if (!tableId) {
                console.error('Unable to lock table because table id is missing', table);
                return;
            }

            // If the table is already locked by us in Redux, just open it
            // without re-issuing the lock — the server returns 409 even for
            // the same user, and that conflict modal would be misleading.
            if (liveTable?.tableLock?.lockedBy && liveTable.tableLock.lockedBy === userId) {
                await openTableInTab(liveTable);
                return;
            }

            const lockResult = await dispatcher(lockTable({
                tableId,
                lockedBy: userId || undefined,
                lockedByName: username || 'Main System',
                deviceType: DEVICE_TYPE.MAIN_SYSTEM,
            }));

            if (lockTable.fulfilled.match(lockResult)) {
                await openTableInTab(lockResult.payload);
                return;
            }

            if (lockTable.rejected.match(lockResult)) {
                const payload = lockResult.payload as any;
                if (payload?.locked && payload?.lockInfo) {
                    const conflictLock = payload.lockInfo as ITableLock;
                    // Server returns 409 even when the requester already
                    // owns the lock from another tab/device. Treat that as
                    // "open it" instead of showing a conflict modal.
                    if (conflictLock.lockedBy === userId) {
                        const owned = liveTable
                            ? { ...liveTable, tableLock: conflictLock }
                            : { ...table, tableLock: conflictLock };
                        await openTableInTab(owned);
                        return;
                    }
                    setLockConflict({ lockInfo: conflictLock, pendingTable: table });
                    return;
                }
                console.error('Unable to lock table before opening', lockResult.payload);
                return;
            }
        } catch (e) {
            console.error(e);
        }
    };

    const EMPTY_TAB_STYLE = {
        width:"100%",
        height:"100%",
        backgroundImage: `url(${BB_BACKGROUND_IMGH})`,
        backgroundSize: '30% 30%', // adjust the size to your liking
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'no-repeat',  
    }

    return (
        <>
        {/* Lock conflict dialog */}
        <Dialog open={!!lockConflict} onClose={() => setLockConflict(null)}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LockIcon color="warning" />
                Table is Locked
            </DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 1 }}>
                    This table is currently open by another user.
                </Alert>
                {lockConflict && (
                    <Stack spacing={0.5} mt={1}>
                        <Typography variant="body2"><b>Locked by:</b> {lockConflict.lockInfo.lockedByName}</Typography>
                        <Typography variant="body2"><b>Device:</b> {lockConflict.lockInfo.deviceType === DEVICE_TYPE.MAIN_SYSTEM ? 'Main System' : 'Tablet'}</Typography>
                        <Typography variant="body2"><b>Since:</b> {new Date(lockConflict.lockInfo.lockedAt).toLocaleTimeString()}</Typography>
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setLockConflict(null)}>Cancel</Button>
            </DialogActions>
        </Dialog>

        <Box sx={ tabs?.length > 0 ? {} :{
           ...EMPTY_TAB_STYLE
        }}>
            <Grid container alignItems="center" bgcolor={theme.palette.background.default} p={2}>
                <Grid item xs={12} md={12} style={{ display: 'flex', alignItems: 'center' }}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        aria-label="dynamic tabs"
                        style={{ height: 50, width: "100%"}}
                    >
                        {tabs?.map((tab, index) => (
                            <Tab
                                key={index}
                                iconPosition="end"
                                sx={{ ...TAB_STYLES }}
                                icon={
                                    <IconButton
                                        size="large"
                                        onClick={(e) => {
                                            e.stopPropagation(); // Prevent triggering tab change when clicking the icon
                                            handleRemoveTab(index);
                                        }}>
                                        {/* <HelpIcon color="info" sx={{ fontSize: 55 }} /> */}
                                        <DisabledByDefaultIcon color="error" sx={{ fontSize: 55 }} />
                                    </IconButton>
                                }
                                label={
                                    <Stack>
                                        <Stack flexDirection={"row"} alignItems={"center"}>
                                            {/* <Typography variant='body1'>TABLE </Typography> */}
                                            {/* <Chip label={tab.label} variant="outlined" /> */}
                                            {tab.no !== 0 && <Chip label={` ${tab.label} ${tab.no}`} variant="outlined" />}
                                            {/* {tab.no !== 0 && <Chip label={`Floor ${tab.floor}`} variant="outlined" />} */}
                                        </Stack>
                                        {/* {tab.floor !== 0 && <Typography fontSize={12} fontWeight={"bold"}>{tab.label}</Typography>} */}
                                    </Stack>
                                }
                            />
                        ))}
                        <Button variant='contained' onClick={handleAddTab} startIcon={<ShoppingCartIcon />}
                            sx={{ borderRadius: 10, minWidth: "150px" }}>
                            NEW / OPEN
                        </Button>
                    </Tabs>
                    <Box mr={5}>
                        <SearchInput
                            value={searchTxt}
                            onChange={(txt) => searchTheProduct(txt)}
                            placeholder="Search by Name, Product Id, Category"
                        />
                    </Box>
                </Grid>
            </Grid>

            {tabs?.map((tab, index) => (
                currentTab === index && (
                    <Box key={index}>
                        {(tab.label === UNASSIGNED_TAB || tab.uniqueTableName === "") ? (
                            <TableAssigner onTableSelect={handleTableSelect} />
                        ) : (
                            <BillingScreen products={products} currentTab={tab.uniqueTableName} tabIndex={index}
                                setCategoryTxt={setCategoryTxt} categories={categories} handleRemoveTab={handleRemoveTab} />
                        )}
                    </Box>
                )
            ))}
        </Box>
        </>
    );
}

export { BillingInTabs };
